import { Media, ProfileMedia, AuditionGroupMedia } from '@minecraft.graphql-operations';
import { gqlMedia, gqlProfileMedia, gqlAuditionGroupMedia } from '@minecraft.graphql-types';
import { DeepPartial } from '@minecraft.utils';
import { MediaVideoProps } from '../types';
import {
  getCloudinary,
  getMediaDownloadOriginalUrl,
  isMediaInColdStorage,
  getMediaNameWithExtension,
  sanitizeDownloadFileName,
  isMediaLocked,
} from './mediaHookUtils';

export const formatMediaToVideo = <T extends DeepPartial<gqlMedia> | DeepPartial<Media>>({
  media,
  startOffset,
}: {
  /** the media object from the network query data */
  media: T;
  /** override to set the start offset for the video poster even if there is a transformation set */
  startOffset?: number;
}): MediaVideoProps => {
  const mediaName = media?.name;
  const mediaNameWithExt = getMediaNameWithExtension({ media });
  const storageStatusCode = media?.mediaStorageStatus?.code;
  const videoCld = getCloudinary({ code: storageStatusCode });
  const isColdStorage = isMediaInColdStorage(storageStatusCode);
  const transformationObj = media?.transformation || media?.thumbnail?.transformation;
  const videoFileKey = media?.fileKey;
  const startOffsetToUse = ['string', 'number'].includes(typeof startOffset)
    ? startOffset
    : transformationObj?.startOffset;

  // cold storage media includes transforms for limiting the video resolution and format
  // this also causes cloudinary to pull the video out of cold storage so that derived assets
  // can be computed on the fly
  const srcRaw = videoCld.video_url(videoFileKey, {
    ...(isColdStorage
      ? {
          crop: 'limit',
          fetchFormat: 'mp4',
          height: '720',
          width: '1280',
          quality: 'auto',
          videoCodec: 'h264',
        }
      : {}),
  });

  const srcPoster = videoCld.video_url(videoFileKey, {
    startOffset: startOffsetToUse ?? 'auto',
    ...(isColdStorage
      ? {
          fetchFormat: 'jpg',
          quality: 'auto',
        }
      : {}),
  });

  const downloadSrc = videoCld.video_url(videoFileKey, {
    // eslint-disable-next-line camelcase
    force_version: false,
    flags: `attachment:${sanitizeDownloadFileName(mediaName)}`,
  });

  return {
    src: srcRaw,
    poster: isColdStorage ? srcPoster : `${srcPoster}.jpg`,
    downloadUrl: getMediaDownloadOriginalUrl({ primaryUrl: downloadSrc }),
    name: mediaName,
    nameWithExt: mediaNameWithExt,
    isLocked: isMediaLocked({ media }),
  };
};

export const formatProfileMediaToVideo = <T extends DeepPartial<gqlProfileMedia> | DeepPartial<ProfileMedia>>({
  profileMedia,
  startOffset,
}: {
  /** the profile media object from the network query data */
  profileMedia: T;
  /** override to set the start offset for the video poster even if there is a transformation set */
  startOffset?: number;
}): MediaVideoProps => {
  return formatMediaToVideo({ media: profileMedia?.media, startOffset });
};

export const formatAuditionGroupMediaToVideo = <
  T extends DeepPartial<gqlAuditionGroupMedia> | DeepPartial<AuditionGroupMedia>
>({
  groupMedia,
  startOffset,
}: {
  /** the audition group media object from the network query */
  groupMedia: T;
  /** override to set the start offset for the video poster even if there is a transformation set */
  startOffset?: number;
}): MediaVideoProps => {
  const media = groupMedia?.media;
  const name = groupMedia?.name || media?.name;
  return formatMediaToVideo({ media: { ...media, name }, startOffset });
};
